import type {
    ExtensionLogger,
    ImportListReportV2Parameters,
    ImportALPV2Parameters,
    ImportObjectPageV2Parameters,
    ImportAllConfigsParameters
} from '@sap/ux-specification-types';
import { LogSeverity, MANIFESTPATH, PageTypeV2, ManifestSection, v2, SchemaType } from '@sap/ux-specification-types';
import { AppProvider } from '../../../common/appProvider';
import { transferSettingsOfObject } from '../common';
// Temporary solution:
// pageAccess.ts will reroute function createListReportConfig to pages/listReport.ts.
// For manual tests this can be changed by switching useGenericSchemaHandling in pageAccess.ts to true.
// In this case generic schema handling an the schema for ListReportNew will be used.
import { createListReportConfig } from '../../genericSchemaHandling/pages/pageAccess';
import { createAnalyticalListPageConfig } from '../pages/analyticalListPage';
import { createObjectPageConfigV2 } from '../pages/objectPage';
import { AppSettings } from '../../application';
import { getSchemaFilePath, generatePageId, getPageTypeV2 } from '../../../common';
import i18next from 'i18next';
import { initI18n } from '../../../../i18n/i18n';
import { log } from '../../../../extensionLogger';

/**
 * Method which generates id for passed page.
 *
 * @param page - Generic Page object.
 * @param pageName - Page name in manifest. Used if entitySet is empty
 * @returns {string} Generated page id
 */
function generateId(page: v2.SapUiAppPageV2, pageName: string): string {
    if (page.component?.name) {
        return generatePageId(getPageTypeV2(page.component?.name), page.entitySet, undefined, pageName);
    } else {
        return pageName;
    }
}

/**
 * Sets up page configurations and navigations for a page.
 *
 * @param page - The page object to configure navigations for.
 * @param v2Page - The V2 page definition from the manifest.
 * @param importParameters - Parameters required for importing configurations.
 */
function getPageConfigsAndNavigations(
    page: v2.Page,
    v2Page: v2.SapUiAppPageV2,
    importParameters: ImportAllConfigsParameters,
    pages: { [key: string]: v2.Page }
) {
    page.navigation = {};

    const targets: { [key: string]: v2.Page } = getPagesAndConfigs(v2Page.pages, importParameters);
    for (const pageId in targets) {
        const target = targets[pageId];
        pages[pageId] = target;
    }
    for (const v2ChildPageName in v2Page.pages) {
        const id = generateId(v2Page.pages[v2ChildPageName], v2ChildPageName);
        if (page.entitySet) {
            page.navigation[id] = targets[id].navigationProperty
                ? `${page.entitySet}.${targets[id].navigationProperty}`
                : page.entitySet;
        } else {
            page.navigation[id] = targets[id].navigationProperty ? targets[id].navigationProperty : id;
        }
    }
}

/**
 * Sets up page definitions, including the configurations for them.
 *
 * @param v2Pages - list of all pages
 * @param importParameters - Parameters required for importing all configurations.
 * @returns an index table of page definitions
 */
function getPagesAndConfigs(
    v2Pages: v2.SapUiAppPagesV2,
    importParameters: ImportAllConfigsParameters
): { [key: string]: v2.Page } {
    const pages: { [key: string]: v2.Page } = {};
    for (const v2PageName in v2Pages) {
        const v2Page = v2Pages[v2PageName];
        const id = generateId(v2Page, v2PageName);
        if (!id) {
            continue;
        }
        const page: v2.Page = {
            pageType: getPageTypeV2(v2Page.component?.name),
            entitySet: v2Page.entitySet,
            ...(v2Page.navigationProperty && { navigationProperty: v2Page.navigationProperty }),
            ...(v2Page.defaultLayoutType && { defaultLayoutType: v2Page.defaultLayoutType })
        };
        pages[id] = page;

        // Get the JSON schema for the page. If no page specific schema exists, fall back top the generic schema for the page type
        const schemaKey: string =
            Object.keys(importParameters.appSchemas).find((element) => element.includes(id)) ||
            Object.keys(importParameters.appSchemas).find((element) => element.includes(id.slice(0, -1))) ||
            Object.keys(importParameters.appSchemas).find((element) => element.includes(page.pageType));

        // convert settings to config
        if (schemaKey) {
            if (page.pageType === PageTypeV2.ListReport) {
                const importLRParameters: ImportListReportV2Parameters = {
                    manifest: importParameters.manifest,
                    flex: importParameters.flexChanges,
                    jsonSchema: importParameters.appSchemas[schemaKey],
                    entitySet: page.entitySet,
                    fragments: importParameters.fragments,
                    logger: importParameters.logger
                };
                page.config = createListReportConfig(importLRParameters);
            } else if (page.pageType === PageTypeV2.AnalyticalListPage) {
                const importALPParameters: ImportALPV2Parameters = {
                    manifest: importParameters.manifest,
                    flex: importParameters.flexChanges,
                    jsonSchema: importParameters.appSchemas[schemaKey],
                    entitySet: page.entitySet,
                    fragments: importParameters.fragments,
                    logger: importParameters.logger
                };
                page.config = createAnalyticalListPageConfig(importALPParameters);
            } else {
                const importOPParameters: ImportObjectPageV2Parameters = {
                    manifest: importParameters.manifest,
                    flex: importParameters.flexChanges,
                    jsonSchema: importParameters.appSchemas[schemaKey],
                    entitySet: page.entitySet,
                    fragments: importParameters.fragments,
                    logger: importParameters.logger
                };
                page.config = createObjectPageConfigV2(importOPParameters);
            }
        }
        if (v2Page.pages) {
            getPageConfigsAndNavigations(page, v2Page, importParameters, pages);
        }
    }
    return pages;
}

/**
 * The V2AppProvider collects all the app information for a Fiori Elements V2 app
 *
 * @param {ImportAllConfigsParameters} importParameters
 */
export class V2AppProvider extends AppProvider {
    /**
     *
     * @param importParameters - Parameters required for importing all configurations.
     */
    public constructor(importParameters: ImportAllConfigsParameters) {
        initI18n();
        let v2Pages: v2.SapUiAppPagesV2;
        if (!importParameters.manifest[ManifestSection.generic]) {
            log(importParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOFE'),
                location: {
                    path: MANIFESTPATH,
                    range: [ManifestSection.generic]
                }
            });
        } else {
            v2Pages = importParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2;
        }
        if (!v2Pages) {
            log(importParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOPAGES', { appId: importParameters.manifest['sap.app']['id'] }),
                location: {
                    path: MANIFESTPATH,
                    range: [ManifestSection.generic]
                }
            });
        }
        const pageKey = v2Pages && Object.keys(v2Pages)[0];
        const pageId = v2Pages && generateId(v2Pages[pageKey], pageKey);
        // Import settings
        const appSettings = new AppSettings();
        const schemaFile = getSchemaFilePath(SchemaType.Application);
        const appSchema = importParameters.appSchemas[schemaFile.filename];
        transferSettingsOfObject(
            appSettings,
            importParameters.manifest,
            appSchema['definitions']['AppSettings'],
            undefined
        );
        // Call base
        super(importParameters, pageId, getPagesAndConfigs(v2Pages, importParameters), appSettings);
    }
}

/**
 * Collects all pages of a V2 app from the manifest, by traversing the V2 page hierarchy.
 *
 * @param v2Pages - entry point = complex page definition in manifest
 * @param componentName - Component name
 * @param logger - Logger instance used for logging errors or information.
 * @returns index table of pages
 */
export function getPages(v2Pages: v2.SapUiAppPagesV2, componentName: string, logger?: ExtensionLogger): v2.Pages {
    const pages: v2.Pages = {};
    for (const v2PageName in v2Pages) {
        const v2Page = v2Pages[v2PageName];
        if (!v2Page.component?.name) {
            log(logger, {
                severity: LogSeverity.Error,
                message: i18next.t('COMPONENTNOTDEFINED', { pageName: v2PageName }),
                location: {
                    path: MANIFESTPATH
                }
            });
        }
        if (v2Page.component?.name === componentName) {
            const id = generateId(v2Page, v2PageName);
            pages[id] = v2Page;
        }
        if (componentName === v2.FE_TEMPLATE_V2_OBJECT_PAGE && v2Page.pages) {
            const subPages = getPages(v2Page.pages, componentName, logger);
            for (const subPageId in subPages) {
                pages[subPageId] = subPages[subPageId];
            }
        }
    }
    return pages;
}
